import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { CreditCard } from 'data/shared/CreditCard';
import {
  shortToCardId,
  cardIdToShort,
} from 'data/shared/cardShortCodes';
import { walletTemplates } from 'data/shared/walletTemplates';
import { CreditCardDataFiltered } from 'data/shared/CreditCardData';
import { fetchSavedWallet } from '../services/walletShareApi';

const PERIOD_DELIMITER = '.';

/**
 * Gets cards from a wallet template by ID
 */
function getCardsFromWalletTemplate(walletId: string): CreditCard[] {
  const template = walletTemplates.find((w) => w.id === walletId);
  if (!template) {
    return [];
  }
  return template.cards.filter((cardId) => CreditCardDataFiltered[cardId]);
}

export const useCreditCardState: () => [
  CreditCard[],
  Dispatch<SetStateAction<CreditCard[]>>
] = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { code: pathCode } = useParams<{ code: string }>();

  // Support ?cards= as alias for ?w= (rewrite immediately)
  const cardsParam = searchParams.get('cards');
  if (cardsParam) {
    searchParams.delete('cards');
    searchParams.set('w', cardsParam);
    setSearchParams(searchParams, { replace: true });
  }

  const wParam = searchParams.get('w');

  // Check if /w/:code matches a wallet template
  const pathIsWalletTemplate = pathCode
    ? walletTemplates.some((w) => w.id === pathCode)
    : false;

  // Saved wallet: /w/<code> when not a template
  const savedParam =
    pathCode && !pathIsWalletTemplate ? pathCode : null;

  // --- Initialization priority ---
  // 1. ?w=CSP.CFU → edit mode
  // 2. /w/:template-id → view mode
  // 3. /w/:saved-code → empty (async fetch), view mode
  let initialCards: CreditCard[] = [];
  let initialIsViewMode = false;

  if (wParam) {
    initialCards = wParam
      .split(PERIOD_DELIMITER)
      .map((code) => shortToCardId(code));
  } else if (pathIsWalletTemplate && pathCode) {
    initialCards = getCardsFromWalletTemplate(pathCode);
    initialIsViewMode = true;
  } else if (savedParam) {
    initialIsViewMode = true;
  }

  const [creditCards, setCreditCardsInternal] =
    useState<CreditCard[]>(initialCards);
  const [isViewMode, setIsViewMode] = useState(initialIsViewMode);
  const isInitialMount = useRef(true);

  // Public setter: any user edit switches to edit mode
  const setCreditCards = useCallback(
    (action: SetStateAction<CreditCard[]>) => {
      setCreditCardsInternal(action);
      setIsViewMode(false);
    },
    []
  );

  // Async fetch for saved wallets (/w/:code)
  useEffect(() => {
    let isActive = true;

    if (!savedParam) {
      return () => {
        isActive = false;
      };
    }

    const loadSavedWallet = async () => {
      try {
        const savedWallet = await fetchSavedWallet(savedParam);
        const validCards = savedWallet.walletIds.filter(
          (cardId) => CreditCardDataFiltered[cardId as CreditCard]
        ) as CreditCard[];

        if (isActive) {
          // Use internal setter — stays in view mode for /w/:code
          setCreditCardsInternal(validCards);
        }
      } catch {
        // Silently fall back to current state if fetch fails
      }
    };

    loadSavedWallet();

    return () => {
      isActive = false;
    };
  }, [savedParam]);

  // URL sync effect
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    if (isViewMode) {
      return;
    }

    const encoded = creditCards.map(cardIdToShort).join(PERIOD_DELIMITER);

    // If we're on /w/:code, navigate to root so the stale code disappears
    if (pathCode) {
      const params = new URLSearchParams(searchParams);
      if (encoded) {
        params.set('w', encoded);
      }
      navigate({ pathname: '/', search: params.toString() }, { replace: true });
      return;
    }

    if (encoded) {
      searchParams.set('w', encoded);
    } else {
      searchParams.delete('w');
    }
    setSearchParams(searchParams, { replace: true });
  }, [creditCards, isViewMode, searchParams, setSearchParams, navigate, pathCode]);

  return [creditCards, setCreditCards];
};
